import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  isSupportedLanguage,
  resolveLanguage,
  translationResources,
} from "@onetasklist/shared";

// The translation files live in the shared library, so the mobile app reads the same ones.
const LANGUAGE_STORAGE_KEY = "onetasklist.region.language";

const getInitialLanguage = (): string =>
  resolveLanguage(
    window.localStorage.getItem(LANGUAGE_STORAGE_KEY),
    window.navigator.language,
  );

// Screen readers choose their voice from <html lang>, so it follows whatever language i18n
// settles on: the saved one at start-up as well as every later change.
i18n.on("languageChanged", (language) => {
  document.documentElement.lang = language;
});

void i18n.use(initReactI18next).init({
  resources: translationResources,
  lng: getInitialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
});

/**
 * Switches the app's language and remembers it on this device. `saveLanguage` also stores it on the
 * region so emails go out in it; a failed save is ignored, because the switch here must not depend
 * on the network.
 */
export const changeLanguage = async (
  language: string,
  saveLanguage?: (language: string) => Promise<void>,
): Promise<void> => {
  if (!isSupportedLanguage(language)) {
    return;
  }

  await i18n.changeLanguage(language);
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);

  // Keep the ASP.NET Core culture cookie in sync so Login.Web and SelectRegion
  // respect the user's language preference on sign-out/sign-in.
  const cookieValue = `c=${language}|uic=${language}`;
  document.cookie = `.AspNetCore.Culture=${encodeURIComponent(cookieValue)};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;

  try {
    await saveLanguage?.(language);
  } catch {
    // Not retried. The next pick saves it, and the region fills a missing language from the
    // culture cookie set above.
  }
};

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY };
export default i18n;
